import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { paymentsApi, ridesApi } from "@/api/rides.api";
import { queryKeys } from "@/utils/queryKeys";
import type { ListQuery } from "@/types/common";
import type { RideStatus } from "@/types/domain";

/** GET /admin/rides/live — polled as a fallback; also kept fresh by admin:ride:update (useRideRealtimeSync). */
export function useLiveRides(refetchIntervalMs = 15_000) {
  return useQuery({
    queryKey: queryKeys.rides.active(),
    queryFn: () => ridesApi.getLive(),
    refetchInterval: refetchIntervalMs,
    placeholderData: (prev) => prev,
  });
}

export function useScheduledRides(
  query: ListQuery & {
    from?: string;
    to?: string;
    assignment?: "assigned" | "unassigned";
  },
) {
  return useQuery({
    queryKey: queryKeys.rides.scheduled(query),
    queryFn: () => ridesApi.getScheduled(query),
    placeholderData: (prev) => prev,
  });
}

export function useRidesList(
  query: ListQuery & {
    status?: RideStatus;
    from?: string;
    to?: string;
    driverId?: string;
    customerId?: string;
  },
) {
  return useQuery({
    queryKey: queryKeys.rides.list(query),
    queryFn: () => ridesApi.list(query),
    placeholderData: (prev) => prev,
  });
}

export function useRide(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.rides.detail(id ?? ""),
    queryFn: () => ridesApi.getById(id as string),
    enabled: !!id,
  });
}

export function useRefundPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      paymentId,
      amount,
      reason,
    }: {
      paymentId: string;
      rideId: string;
      amount?: number;
      reason: string;
    }) => paymentsApi.refund(paymentId, { amount, reason }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rides.detail(variables.rideId) });
    },
  });
}
